import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LangSwitcher from '@/components/LangSwitcher';
import { SECTORS_ALL } from '@/lib/sectors';

export default function Sectors() {
    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="page-sectors">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src="/african-valley-logo.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover ring-1 ring-border shadow-soft" />
                        <span className="flex items-baseline gap-2">
                            <span className="font-heading text-2xl font-semibold tracking-tight">African</span>
                            <span className="font-heading text-2xl text-primary font-medium">Valley</span>
                            <span className="font-heading text-xs uppercase tracking-[0.18em] text-foreground/50 ml-1.5 hidden sm:inline">Projets 360°</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <LangSwitcher />
                        <Link to="/" className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-1">
                            <ArrowLeft size={14} /> Accueil
                        </Link>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                {/* Hero */}
                <section className="border-b border-border">
                    <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
                        <div className="max-w-3xl">
                            <div className="eyebrow mb-5 text-primary">Secteurs accompagnés</div>
                            <h1 className="font-heading text-4xl lg:text-5xl leading-tight">
                                Des solutions pensées pour les secteurs qui <span className="text-primary">construisent l'Afrique</span>.
                            </h1>
                            <p className="mt-7 text-lg text-foreground/75 leading-[1.8]">
                                Templates métier prêts à l'emploi, workflows sectoriels, conformité documentaire,
                                jalons validés et formations certifiantes — <strong>AFRICAN VALLEY Projets 360°</strong>
                                {' '}accompagne les organisations opérant dans <strong>14 secteurs stratégiques</strong>.
                            </p>
                            <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-secondary/30 bg-secondary-light text-secondary text-xs">
                                <Sparkles size={12} /> Secteurs fondateurs · depuis 2025
                            </div>
                        </div>
                    </div>
                </section>

                {/* All 14 sectors — uniform grid */}
                <section className="bg-surface-muted/30">
                    <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
                        <div className="flex items-baseline justify-between gap-6 flex-wrap mb-10">
                            <div>
                                <div className="eyebrow text-primary mb-3">14 secteurs activés</div>
                                <h2 className="font-heading text-3xl">Une couverture sectorielle complète</h2>
                            </div>
                            <p className="text-sm text-foreground/65 max-w-md">
                                Chaque secteur intègre nativement ses jalons métier, ses templates documentaires
                                et ses cas d'usage pédagogiques dans l'<strong>Espace Formation</strong>.
                            </p>
                        </div>
                        <div className="grid lg:grid-cols-2 gap-6">
                            {SECTORS_ALL.map((s) => (
                                <article key={s.key} className="card-soft p-8 bg-background" data-testid={`sector-${s.key}`}>
                                    <div className="flex items-start gap-4">
                                        <div className="h-12 w-12 rounded-lg bg-primary-light text-primary flex items-center justify-center flex-shrink-0">
                                            <s.icon size={22} strokeWidth={1.6} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-heading text-2xl flex items-center gap-2">
                                                <span aria-hidden="true">{s.emoji}</span> {s.name}
                                            </h3>
                                            <p className="text-foreground/75 mt-2 leading-[1.7] text-[15px]">{s.pitch}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm text-foreground/65 mt-5 leading-[1.8]">{s.long}</p>
                                    <ul className="mt-5 grid sm:grid-cols-2 gap-2.5 text-sm">
                                        {s.bullets.map((b, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <Check size={13} className="text-secondary mt-1 flex-shrink-0" />
                                                <span className="text-foreground/85">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="border-t border-border">
                    <div className="max-w-4xl mx-auto px-6 py-16 text-center">
                        <h3 className="font-heading text-3xl">Votre secteur n'est pas listé ?</h3>
                        <p className="text-foreground/70 mt-3 max-w-2xl mx-auto leading-[1.7]">
                            African Valley Projets 360° s'adapte aux besoins spécifiques de votre organisation.
                            Notre équipe peut configurer un workflow sectoriel sur mesure sous 2 semaines.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                            <Link to="/p/contact">
                                <Button size="lg" className="bg-primary hover:bg-primary-hover" data-testid="sectors-cta-contact">
                                    Discuter de mon secteur <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </Link>
                            <Link to="/apply">
                                <Button size="lg" variant="outline" data-testid="sectors-cta-apply">
                                    Créer mon espace gratuit
                                </Button>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Quiz lead-gen */}
                <section className="border-t border-border bg-gradient-to-br from-primary-light/30 via-surface to-secondary-light/30">
                    <div className="max-w-4xl mx-auto px-6 py-16">
                        <div className="grid md:grid-cols-12 gap-8 items-center">
                            <div className="md:col-span-7">
                                <div className="eyebrow text-primary mb-3 flex items-center gap-2">
                                    <Sparkles size={12} /> Diagnostic gratuit · 2 minutes
                                </div>
                                <h3 className="font-heading text-3xl lg:text-4xl leading-tight">
                                    Quel est votre niveau de maturité <span className="text-primary">pilotage projet</span> en Afrique ?
                                </h3>
                                <p className="text-foreground/70 mt-4 leading-[1.7]">
                                    5 questions pour situer votre organisation entre <strong>Démarrage</strong>,
                                    <strong> Croissance</strong>, <strong>Portefeuille</strong> et <strong>Programme stratégique</strong>.
                                    Recevez une recommandation personnalisée sur le plan African Valley adapté.
                                </p>
                            </div>
                            <div className="md:col-span-5 flex md:justify-end">
                                <Link to="/p/diagnostic" className="w-full md:w-auto">
                                    <Button size="lg" className="w-full md:w-auto bg-primary hover:bg-primary-hover" data-testid="sectors-cta-diagnostic">
                                        Lancer le diagnostic <ArrowRight size={16} className="ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
